// frontend/src/components/AuthWrapper.js — auth gate + minimalist
// pre-auth landing.
//
// Mirrors LandingPage.js's codex-vibe aesthetic: max-w-3xl, semibold
// h1, indigo dot bullets, no decorative emoji icons. Anonymous
// visitors hit this; signed-in users pass through to the routed UI.
import React, { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { Loader2, LogIn } from 'lucide-react';

import { auth, signInWithGoogle } from '../config/firebase';
import Loading from './common/Loading';
import { Button } from './ui';
import { MasteryGlyph, DueMarker, BlockerMarker, BlockerNote } from './ui/mastery';

// Local UI checks only: `npm start` with REACT_APP_DEV_AUTH_BYPASS=1 skips Firebase (the dev
// backend already falls back to the anonymous user). NODE_ENV is "production" in every build,
// so this branch is dead code there.
const DEV_AUTH_BYPASS =
    process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEV_AUTH_BYPASS === '1';

export default function AuthWrapper({ children }) {
    const [user, setUser] = useState(
        DEV_AUTH_BYPASS ? { uid: 'anonymous', email: 'dev@localhost', displayName: 'Dev' } : null,
    );
    const [loading, setLoading] = useState(!DEV_AUTH_BYPASS);

    useEffect(() => {
        if (DEV_AUTH_BYPASS) return undefined;
        const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
            // NEV-FRONT-02: keep only serializable identity fields, not
            // the full firebase user object (which carries auth methods
            // and internals).
            setUser(
                fbUser
                    ? { uid: fbUser.uid, email: fbUser.email, displayName: fbUser.displayName }
                    : null,
            );
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    if (loading) {
        return <Loading message="Checking authentication..." />;
    }
    if (!user) {
        return <LoginPage />;
    }
    return children;
}

const PRINCIPLES = [
    {
        title: 'Tell it what you want',
        body: 'A chat assistant figures out the right course for you. No category trees, no checkboxes.',
    },
    {
        title: 'Practice that adapts',
        body: 'Questions react to what you actually know — generated fresh each session, never recycled.',
    },
    {
        title: 'Memory across sessions',
        body: 'It remembers what you struggled with last time and nudges you back to it when you return.',
    },
];

const PROOF_ROWS = [
    { concept: 'kubectl basics', level: 'mastered' },
    { concept: 'Deployments', level: 'advanced' },
    { concept: 'ClusterIP', level: 'proficient', due: true },
    { concept: 'Pod Networking', level: 'developing', due: true },
    { concept: 'Access Modes', level: 'struggling', blocker: true },
    { concept: 'CNI Plugins', level: 'novice' },
];

function LoginPage() {
    const [signing, setSigning] = useState(false);

    const handleSignIn = async () => {
        setSigning(true);
        try {
            await signInWithGoogle();
        } catch (err) {
            console.error('Sign in failed:', err);
            setSigning(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto px-6 py-16 sm:py-24">
            <header className="mb-12">
                <div className="lh-eyebrow">A tutor with a memory</div>
                <h1 className="land-h1">An AI tutor that <em>remembers</em> exactly where you slip.</h1>
                <p className="land-sub">
                    Not flashcards. A coach that watches your reasoning, names the
                    one concept blocking the rest, and teaches to you — a little
                    sharper every day you return.
                </p>
            </header>

            <div className="mb-20">
                <Button variant="primary" onClick={handleSignIn} disabled={signing}>
                    {signing ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Signing in…
                        </>
                    ) : (
                        <>
                            <LogIn className="w-4 h-4" />
                            Sign in with Google
                        </>
                    )}
                </Button>
            </div>

            {/* memory made visible — the product in one panel */}
            <div className="proof mb-16 max-w-md">
                <div className="proof-head">
                    <div className="ph-dot">A</div>
                    <div style={{ flex: 1 }}><b>What your tutor knows</b><span> · after 3 weeks on CKA</span></div>
                </div>
                <div className="proof-body">
                    <BlockerNote concept="Access Modes" unlocks={['Volume Types', 'PVCs']} />
                    <div className="proof-cap">your mastery · live</div>
                    {PROOF_ROWS.map((r) => (
                        <div className="proof-row" key={r.concept}>
                            <MasteryGlyph level={r.level} />
                            <span className="pr-name">{r.concept}</span>
                            {r.blocker && <BlockerMarker />}
                            {r.due && !r.blocker && <DueMarker />}
                            <span className="pr-level">{r.level}</span>
                        </div>
                    ))}
                </div>
            </div>

            <section className="mb-8">
                <h2 className="text-sm font-medium text-mutedink uppercase tracking-wider mb-6">
                    How it works
                </h2>
                <div className="space-y-6">
                    {PRINCIPLES.map((p) => (
                        <div key={p.title} className="flex gap-4">
                            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                            <div>
                                <h3 className="text-base font-medium text-ink">{p.title}</h3>
                                <p className="text-sm text-mutedink leading-relaxed mt-1">{p.body}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
